<template>
  <div class="page-container">
    <AppSidebar />
    
    <div class="main-content">
      <div class="about-page">
        <div v-if="loading" class="loading-container">
          <div class="spinner"></div>
          <p>加载中...</p>
        </div>
        
        <div v-else-if="error" class="error-container">
          <p>{{ error }}</p>
          <button @click="fetchContent" class="retry-btn">重试</button>
        </div>
        
        <template v-else>
          <div class="page-header">
            <h1>{{ pageData.title }}</h1>
          </div>
          
          <div class="content-container">
            <div v-if="pageData.image_url" class="content-image">
              <img :src="pageData.image_url" :alt="pageData.title">
            </div>
            
            <div class="content-text" v-html="pageData.content"></div>
          </div>
        </template>
      </div> 
      
      <AppFooter />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import { getAboutContent } from '@/api/about';
import AppSidebar from '@/components/layout/Sidebar.vue';
import AppFooter from '@/components/layout/Footer.vue';

export default {
  name: 'AboutPage',
  components: {
    AppSidebar,
    AppFooter
  },
  props: {
    type: {
      type: String,
      required: true,
      validator: (value) => ['about', 'contact', 'agreement', 'privacy', 'statement', 'feedback'].includes(value)
    }
  },
  setup(props) {
    const pageData = ref({});
    const loading = ref(true);
    const error = ref('');
    
    const fetchContent = async () => {
      try {
        loading.value = true;
        error.value = '';
        const response = await getAboutContent(props.type);
        
        if (response.code === 1 && response.data) {
          pageData.value = response.data;
          // 设置页面标题
          document.title = `${pageData.value.title} - 番剧动漫`;
        } else {
          error.value = response.msg || '获取内容失败';
        }
      } catch (err) {
        error.value = '网络错误，请稍后重试';
        console.error('获取内容失败:', err);
      } finally {
        loading.value = false;
      }
    };
    
    onMounted(() => {
      fetchContent();
    });
    
    // 当路由参数变化时重新获取内容
    watch(() => props.type, () => {
      fetchContent();
    });
    
    return {
      pageData,
      loading,
      error,
      fetchContent
    };
  }
};
</script>

<style scoped>
.page-container {
  display: flex;
  min-height: 100vh;
  background-color: #f5f6fa;
}

.main-content {
  flex: 1;
  margin-left: 200px; /* 侧边栏宽度 */
  display: flex;
  flex-direction: column;
  min-height: 100vh; /* 确保主内容区域至少占满视口高度 */
}

.about-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px;
  flex: 1; /* 让内容区域占据剩余空间 */
  width: 100%;
}

.page-header {
  text-align: center;
  margin-bottom: 40px;
}

.page-header h1 {
  font-size: 30px;
  color: #333;
  margin-bottom: 10px;
  font-weight: 600;
  position: relative;
  display: inline-block;
  padding-bottom: 15px;
}

.page-header h1:after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, #ff6b81, #ff9eb6);
  border-radius: 3px;
}

.content-container {
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.08);
}

.content-image {
  margin-bottom: 20px;
  border-radius: 8px;
  overflow: hidden;
}

.content-image img {
  width: 100%;
  height: auto;
  display: block;
}

.content-text {
  line-height: 1.8;
  color: #444;
  font-size: 16px;
}

.content-text h2 {
  font-size: 22px;
  margin: 25px 0 15px;
  color: #333;
}

.content-text h3 {
  font-size: 18px;
  margin: 20px 0 10px;
  color: #444;
}

.content-text p {
  margin-bottom: 15px;
}

.content-text ul, .content-text ol {
  margin-bottom: 15px;
  padding-left: 20px;
}

.content-text li {
  margin-bottom: 8px;
}

.loading-container, .error-container {
  background: white;
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  margin-bottom: 30px;
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.08);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 107, 129, 0.3);
  border-radius: 50%;
  border-top-color: #ff6b81;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-container p {
  color: #e74c3c;
  margin-bottom: 20px;
}

.retry-btn {
  background-color: #ff6b81;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s;
}

.retry-btn:hover {
  background-color: #ff5a72;
  transform: translateY(-2px);
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
  }
  
  .about-page {
    padding: 20px 15px;
  }
  
  .content-container {
    padding: 20px;
  }
}
</style> 